import React, { useState } from 'react';
import '../caria.css';
import { VehicleDraftForm } from '../types';

/**
 * VehicleDraftCreate (Epic 6, S6.8) — initial draft form.
 *
 * Captures the 8 spec fields the backlog requires ([F]: make, model,
 * year, mileage, transmission, fuel, color, VIN). Mobile-first single
 * column; controlled local state with client-side validation hints.
 * Submit calls the optional `onCreate` prop (default noop) — no network
 * here by design, consistent with the Epic 4 skeletons. Epic 7 wires
 * POST /vehicles.
 */
export interface VehicleDraftCreateProps {
  initial?: Partial<VehicleDraftForm>;
  submitting?: boolean;
  onCreate?: (form: VehicleDraftForm) => void;
  onCancel?: () => void;
}

const TRANSMISSIONS = ['Automática', 'Manual', 'CVT'];
const FUELS = ['Gasolina', 'Diésel', 'Híbrido', 'Eléctrico'];

const emptyForm: VehicleDraftForm = {
  make: '',
  model: '',
  year: '',
  mileageKm: '',
  transmission: '',
  fuel: '',
  color: '',
  vin: '',
};

type Errors = Partial<Record<keyof VehicleDraftForm, string>>;

const currentYear = new Date().getFullYear();

const validate = (form: VehicleDraftForm): Errors => {
  const errors: Errors = {};
  if (!form.make.trim()) errors.make = 'Indica la marca.';
  if (!form.model.trim()) errors.model = 'Indica el modelo.';
  const year = Number(form.year);
  if (!form.year.trim()) errors.year = 'Indica el año.';
  else if (!Number.isInteger(year) || year < 1950 || year > currentYear + 1)
    errors.year = `Año entre 1950 y ${currentYear + 1}.`;
  const km = Number(form.mileageKm);
  if (!form.mileageKm.trim()) errors.mileageKm = 'Indica el kilometraje.';
  else if (!Number.isFinite(km) || km < 0) errors.mileageKm = 'Kilometraje inválido.';
  if (!form.transmission) errors.transmission = 'Selecciona la transmisión.';
  if (!form.fuel) errors.fuel = 'Selecciona el combustible.';
  if (!form.color.trim()) errors.color = 'Indica el color.';
  // VIN is optional at draft time (verified later in the VIN tab), but
  // if provided it must be the canonical 17 chars (mirrors S6.3).
  if (form.vin.trim() && form.vin.trim().length !== 17)
    errors.vin = 'El VIN debe tener 17 caracteres.';
  return errors;
};

export const VehicleDraftCreate: React.FC<VehicleDraftCreateProps> = ({
  initial,
  submitting = false,
  onCreate,
  onCancel,
}) => {
  const [form, setForm] = useState<VehicleDraftForm>({ ...emptyForm, ...initial });
  const [touched, setTouched] = useState(false);

  const errors = validate(form);
  const isValid = Object.keys(errors).length === 0;

  const set =
    (field: keyof VehicleDraftForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!isValid) return;
    onCreate?.(form);
  };

  const fieldError = (field: keyof VehicleDraftForm) =>
    touched && errors[field] ? (
      <span className="caria-publish-field__error" data-testid={`draft-error-${field}`}>
        {errors[field]}
      </span>
    ) : null;

  return (
    <main className="caria-publish" data-testid="vehicle-draft-create">
      <h1>Nuevo vehículo</h1>
      <p className="caria-publish__lead">
        Captura los datos básicos para crear el borrador. Podrás añadir VIN, fotos, declaración y
        precio en el siguiente paso.
      </p>

      <form className="caria-publish-form" onSubmit={handleSubmit} noValidate>
        <label className="caria-publish-field">
          <span>Marca</span>
          <input value={form.make} onChange={set('make')} placeholder="Toyota" />
          {fieldError('make')}
        </label>

        <label className="caria-publish-field">
          <span>Modelo</span>
          <input value={form.model} onChange={set('model')} placeholder="Corolla" />
          {fieldError('model')}
        </label>

        <label className="caria-publish-field">
          <span>Año</span>
          <input
            type="number"
            inputMode="numeric"
            value={form.year}
            onChange={set('year')}
            placeholder={String(currentYear)}
          />
          {fieldError('year')}
        </label>

        <label className="caria-publish-field">
          <span>Kilometraje (km)</span>
          <input
            type="number"
            inputMode="numeric"
            value={form.mileageKm}
            onChange={set('mileageKm')}
            placeholder="64000"
          />
          {fieldError('mileageKm')}
        </label>

        <label className="caria-publish-field">
          <span>Transmisión</span>
          <select value={form.transmission} onChange={set('transmission')}>
            <option value="">Selecciona…</option>
            {TRANSMISSIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {fieldError('transmission')}
        </label>

        <label className="caria-publish-field">
          <span>Combustible</span>
          <select value={form.fuel} onChange={set('fuel')}>
            <option value="">Selecciona…</option>
            {FUELS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          {fieldError('fuel')}
        </label>

        <label className="caria-publish-field">
          <span>Color</span>
          <input value={form.color} onChange={set('color')} placeholder="Blanco" />
          {fieldError('color')}
        </label>

        <label className="caria-publish-field">
          <span>VIN (opcional ahora)</span>
          <input
            value={form.vin}
            onChange={set('vin')}
            placeholder="1HGCM82633A004352"
            maxLength={17}
          />
          {fieldError('vin')}
        </label>

        <div className="caria-publish-form__actions">
          {onCancel && (
            <button type="button" className="caria-btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
          )}
          <button
            type="submit"
            className="caria-cta"
            disabled={submitting || (touched && !isValid)}
            data-testid="draft-submit"
          >
            {submitting ? 'Creando…' : 'Crear borrador'}
          </button>
        </div>
      </form>
    </main>
  );
};

export default VehicleDraftCreate;
